import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Gift, User, MapPin, Clock, Package, Shield, AlertCircle } from 'lucide-react'
import { validateTransferToken, claimTransferredOrder, TransferStatus } from '../lib/transfers'
import { formatPrice } from '../lib/square'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import { EnterpriseToast } from '../components/ui/EnterpriseToast'

const TransferPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, signUp, signUpWithPhone } = useAuth()
  
  const [transferStatus, setTransferStatus] = useState<TransferStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [claiming, setClaiming] = useState(false)
  const [showSignup, setShowSignup] = useState(false)
  const [newUserData, setNewUserData] = useState({
    firstName: '',
    lastName: '',
    zipCode: '',
    email: '',
    phone: '',
    password: ''
  })

  useEffect(() => {
    if (!token) {
      navigate('/')
      return
    }

    const checkTransfer = async () => {
      try {
        setLoading(true)
        const status = await validateTransferToken(token)
        setTransferStatus(status)
      } catch (error) {
        console.error('Failed to validate transfer token:', error)
        setTransferStatus({
          isValid: false,
          isExpired: false,
          isClaimed: false,
          error: 'Failed to validate transfer'
        })
      } finally {
        setLoading(false)
      }
    }

    checkTransfer()
  }, [token, navigate])

  const handleClaimTransfer = async () => {
    if (!transferStatus?.isValid || !token) return

    setClaiming(true)
    try {
      const result = await claimTransferredOrder(
        { transferToken: token, newUserData: showSignup ? newUserData : undefined },
        user?.id
      )

      if (result.success) {
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            visible={t.visible}
            type="success"
            title="Transfer Complete"
            message="The order has been transferred to your account"
          />
        ))
        navigate(result.orderId ? `/orders/${result.orderId}` : '/orders')
      } else {
        toast.custom((t) => (
          <EnterpriseToast
            id={t.id}
            visible={t.visible}
            type="error"
            title="Transfer Failed"
            message={result.error || 'Failed to claim transfer'}
          />
        ))
      }
    } catch (error) {
      console.error('Failed to claim transfer:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="error"
          title="Transfer Failed"
          message="Could not claim transfer. Please try again"
        />
      ))
    } finally {
      setClaiming(false)
    }
  }

  const handleSignupAndClaim = async () => {
    if (!newUserData.firstName || !newUserData.lastName || !newUserData.zipCode || !newUserData.password) {
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="error"
          title="Missing Information"
          message="Please fill in all required fields"
        />
      ))
      return
    }

    setClaiming(true)
    try {
      // Determine if it's email or phone signup
      const isEmail = newUserData.email.length > 0
      const userData = {
        first_name: newUserData.firstName,
        last_name: newUserData.lastName,
        zip_code: newUserData.zipCode
      }

      if (isEmail) {
        await signUp(newUserData.email, newUserData.password, userData)
      } else {
        await signUpWithPhone(newUserData.phone, newUserData.password, userData)
      }

      // After signup, claim the transfer
      setTimeout(() => {
        handleClaimTransfer()
      }, 1000)

    } catch (error) {
      console.error('Signup failed:', error)
      toast.custom((t) => (
        <EnterpriseToast
          id={t.id}
          visible={t.visible}
          type="error"
          title="Account Creation Failed"
          message="Could not create account. Please try again"
        />
      ))
      setClaiming(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-secret-bg-primary">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!transferStatus) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-secret-bg-primary">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold text-secret-text-primary mb-2">Invalid Transfer</h2>
          <p className="text-secret-text-secondary mb-6">This transfer link is not valid.</p>
          <Link to="/" className="secret-btn-primary">
            Return Home
          </Link>
        </div>
      </div>
    )
  }

  if (!transferStatus.isValid) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-secret-bg-primary">
        <div className="text-center max-w-md mx-auto px-4">
          {transferStatus.isExpired && (
            <>
              <Clock size={48} className="mx-auto mb-4 text-orange-500" />
              <h2 className="text-2xl font-bold text-secret-text-primary mb-2">Transfer Expired</h2>
              <p className="text-secret-text-secondary mb-6">
                This transfer link has expired. Transfer links are valid for 24 hours only.
              </p>
            </>
          )}
          
          {transferStatus.isClaimed && (
            <>
              <Gift size={48} className="mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold text-secret-text-primary mb-2">Already Claimed</h2>
              <p className="text-secret-text-secondary mb-6">
                This order has already been claimed by someone else.
              </p>
            </>
          )}
          
          {!transferStatus.isExpired && !transferStatus.isClaimed && (
            <>
              <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
              <h2 className="text-2xl font-bold text-secret-text-primary mb-2">Invalid Transfer</h2>
              <p className="text-secret-text-secondary mb-6">
                {transferStatus.error || 'This transfer link is not valid.'}
              </p>
            </>
          )}

          <Link to="/" className="secret-btn-primary">
            Return Home
          </Link>
        </div>
      </div>
    )
  }

  const { order, fromUser } = transferStatus
  
  return (
    <div className="min-h-screen-safe bg-secret-bg-primary">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <Gift size={48} className="mx-auto mb-4 text-secret-primary" />
          <h1 className="text-3xl font-bold text-secret-text-primary mb-2">You've Received an Order!</h1>
          <p className="text-secret-text-secondary">
            <strong>{fromUser.first_name} {fromUser.last_name}</strong> has transferred their SECRET order to you
          </p>
        </div>

        {/* Order Details */}
        <div className="bg-secret-bg-secondary rounded-lg p-6 mb-6">
          <h3 className="text-xl font-bold text-secret-text-primary mb-4 flex items-center space-x-2">
            <Package size={20} />
            <span>Order Details</span>
          </h3>
          
          <div className="space-y-4">
            {/* Order Summary */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-secret-text-secondary">Order Number:</span>
                <div className="font-medium text-secret-text-primary">{order.order_number}</div>
              </div>
              <div>
                <span className="text-secret-text-secondary">Total Amount:</span>
                <div className="font-bold text-secret-primary">{formatPrice(order.total_amount * 100)}</div>
              </div>
            </div>

            {/* Event Info */}
            {order.secret_events && (
              <div className="bg-secret-bg-primary rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="text-secret-primary mt-1" size={20} />
                  <div className="flex-1">
                    <h4 className="font-medium text-secret-text-primary">{order.secret_events.title}</h4>
                    <p className="text-secret-text-secondary">{order.secret_events.location_name}</p>
                    <div className="flex items-center space-x-2 text-secret-text-secondary text-sm mt-1">
                      <Clock size={14} />
                      <span>
                        {new Date(order.secret_events.event_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Order Items */}
            {order.secret_order_items && order.secret_order_items.length > 0 && (
              <div>
                <h4 className="font-medium text-secret-text-primary mb-2">Items:</h4>
                <div className="space-y-2">
                  {order.secret_order_items.map((item: any, index: number) => (
                    <div key={index} className="flex justify-between items-start text-sm">
                      <div className="flex-1">
                        <span className="text-secret-text-primary">{item.secret_menu_items?.name || 'Item'}</span>
                        <div className="text-secret-text-secondary">
                          Qty: {item.quantity} × {formatPrice(item.unit_price * 100)}
                        </div>
                      </div>
                      <div className="text-secret-text-primary font-medium ml-4">
                        {formatPrice(item.total_price * 100)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Claim Section */}
        <div className="bg-secret-bg-secondary rounded-lg p-6">
          <h3 className="text-xl font-bold text-secret-text-primary mb-4">Claim This Order</h3>
          
          {user ? (
            // User is logged in
            <div className="space-y-4">
              <div className="bg-green-600/20 border border-green-600 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield size={16} className="text-green-400" />
                  <span className="font-medium text-green-400">Ready to claim as {user.email || user.phone}</span>
                </div>
                <p className="text-green-200 text-sm">
                  Once you claim this order, it will be transferred to your account and you'll receive pickup instructions.
                </p>
              </div>
              
              <button
                onClick={handleClaimTransfer}
                disabled={claiming}
                className="secret-btn-primary w-full flex items-center justify-center space-x-2"
              >
                {claiming ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Gift size={16} />
                    <span>Claim Order</span>
                  </>
                )}
              </button>
            </div>
          ) : (
            // User needs to sign up or log in
            <div className="space-y-4">
              {!showSignup ? (
                <>
                  <p className="text-secret-text-secondary mb-4">
                    To claim this order, you need a SECRET account. You can sign in if you already have one, or create a new account.
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Link to="/login" className="secret-btn-secondary text-center">
                      Sign In
                    </Link>
                    <button
                      onClick={() => setShowSignup(true)}
                      className="secret-btn-primary"
                    >
                      Create Account
                    </button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <h4 className="font-medium text-secret-text-primary">Create Your SECRET Account</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secret-text-primary mb-2">First Name *</label>
                      <input
                        type="text"
                        value={newUserData.firstName}
                        onChange={(e) => setNewUserData(prev => ({ ...prev, firstName: e.target.value }))}
                        className="secret-input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secret-text-primary mb-2">Last Name *</label>
                      <input
                        type="text"
                        value={newUserData.lastName}
                        onChange={(e) => setNewUserData(prev => ({ ...prev, lastName: e.target.value }))}
                        className="secret-input"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secret-text-primary mb-2">Email Address *</label>
                    <input
                      type="email"
                      value={newUserData.email}
                      onChange={(e) => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                      className="secret-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secret-text-primary mb-2">Zip Code *</label>
                    <input
                      type="text"
                      value={newUserData.zipCode}
                      onChange={(e) => setNewUserData(prev => ({ ...prev, zipCode: e.target.value }))}
                      className="secret-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secret-text-primary mb-2">Password *</label>
                    <input
                      type="password"
                      value={newUserData.password}
                      onChange={(e) => setNewUserData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Minimum 8 characters"
                      className="secret-input"
                      minLength={8}
                      required
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      onClick={() => setShowSignup(false)}
                      className="secret-btn-secondary flex-1"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleSignupAndClaim}
                      disabled={claiming}
                      className="secret-btn-primary flex-1 flex items-center justify-center space-x-2"
                    >
                      {claiming ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <>
                          <User size={16} />
                          <span>Create Account & Claim</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Security Notice */}
        <div className="bg-secret-bg-tertiary rounded-lg p-4 mt-6">
          <h4 className="font-medium text-secret-text-primary mb-2 flex items-center space-x-2">
            <Shield size={16} className="text-secret-primary" />
            <span>Transfer Security</span>
          </h4>
          <ul className="text-secret-text-secondary text-sm space-y-1">
            <li>• This transfer link expires in 24 hours from creation</li>
            <li>• Each link can only be used once</li>
            <li>• Both parties receive confirmation notifications</li>
            <li>• The order will be transferred to your SECRET account</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default TransferPage